/* eslint-disable no-param-reassign */
import uniqBy from 'lodash/uniqBy';
import { PayloadAction } from '@reduxjs/toolkit';
import { PermissionsState } from '../permissions.slice';
import Group from '../../models/groups';
import Membership from '../../models/membership';

type GroupToggle = {
  group: Group;
  members: Membership[];
  ownerRoles: string[];
  selectedMembers: Membership[];
}

export default function toggleGroup(state: PermissionsState, action: PayloadAction<GroupToggle>) {
  const { payload: { group, members, ownerRoles } } = action;
  const { selectedGroups } = state;
  const index = selectedGroups.findIndex((g) => g.id === group.id);
  const selectedGroupsMembersIds = selectedGroups.flatMap((selectedGroup) => selectedGroup.memberIds);

  if (index > -1) {
    selectedGroups.splice(index, 1);
    if (group.memberIds) {
      // When removing a single group we need to be sure not to remove owners and members that are in another group

      let duplicateGroupMembers: Membership[] = [];
      let membersToRemove: Membership[] = members
        .filter((member) => group.memberIds!
          .includes(member.id)) // remove group members
        .filter((toRemove) => !ownerRoles.includes(toRemove.roleName)); // keep owner roles

      const duplicates = selectedGroupsMembersIds.filter((id, idx, arr) => arr.indexOf(id) !== idx);

      if (duplicates.length) {
        duplicateGroupMembers = members.filter((member) => duplicates.includes(member.id));
        membersToRemove = membersToRemove
          .filter((member) => !duplicateGroupMembers // keep duplicate members selected
            .some((groupMember) => member.id === groupMember.id));
      }

      const removedFromSelected = action.payload.selectedMembers
        .filter((member) => !membersToRemove
          .map((toRemove) => toRemove.id)
          .includes(member.id)); // keeps all members except 'members to remove'

      const membersRemovedFromSelected = !membersToRemove.length
        ? action.payload.selectedMembers : removedFromSelected;

      state.selectedMembers = uniqBy([...membersRemovedFromSelected], 'id');
    }
  } else {
    selectedGroups.push(group);

    if (group.memberIds) {
      const membersInGroup = members
        .filter((member) => group.memberIds!
          .some((memberId) => member.id === memberId));
      if (membersInGroup.length) {
        state.selectedMembers = uniqBy([...action.payload.selectedMembers, ...membersInGroup], 'id');
      }
    }
  }
}
